import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BsStar, BsStarFill, BsStarHalf } from "react-icons/bs";

type StarRatingCompProps = {
  stars: number;
  callBack?: (rating: number) => void;
};

interface StarItem {
  fill: boolean;
  half: boolean;
  rate: number;
}

const StarRatingComp: React.FC<StarRatingCompProps> = ({
  stars = 0,
  callBack,
}: StarRatingCompProps): JSX.Element => {
  const defaultStarList: StarItem[] = useMemo(
    () => [
      { fill: false, half: false, rate: 1 },
      { fill: false, half: false, rate: 2 },
      { fill: false, half: false, rate: 3 },
      { fill: false, half: false, rate: 4 },
      { fill: false, half: false, rate: 5 },
    ],
    [],
  );

  const [star, setStar] = useState<StarItem[]>(defaultStarList);
  const [starList, setStarList] = useState<StarItem[]>(defaultStarList);

  useEffect(() => {
    if (stars === undefined || stars === null) return;

    const starValue = Number(stars);
    if (isNaN(starValue)) return;

    const newStarList = defaultStarList.map((star) => ({ ...star }));
    for (let index = 0; index < Math.floor(starValue); index++) {
      if (index < 5) {
        newStarList[index].fill = true;
      }
    }

    if (starValue % 1 !== 0) {
      const indexToHalfFill = Math.floor(starValue);
      if (indexToHalfFill < 5) {
        newStarList[indexToHalfFill].half = true;
        newStarList[indexToHalfFill].fill = true;
      }
    }

    setStar(newStarList);
    setStarList(newStarList);
  }, [stars, defaultStarList]);

  const handleMouseOver = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>, rate: number) => {
      if (!callBack) return;

      const target = e.currentTarget;
      const iconRect = target.getBoundingClientRect();
      const offsetX = e.clientX - iconRect.left;
      const widthPercentage = (offsetX / iconRect.width) * 100;
      const hoverPercentage = Math.min(Math.max(widthPercentage, 0), 100);
      const newStarList = defaultStarList.map((star) => ({ ...star }));

      if (hoverPercentage >= 50) {
        for (let index = 0; index < rate; index++) {
          newStarList[index].fill = true;
        }
      } else {
        for (let index = 0; index < rate; index++) {
          newStarList[index].fill = true;
          if (index === rate - 1) {
            newStarList[index].half = true;
          }
        }
      }
      setStar(newStarList);
    },
    [callBack, defaultStarList],
  );

  const handleClick = useCallback(() => {
    if (!callBack) return;

    setStarList(star);
    let val = 0;

    for (let i = 0; i < star.length; i++) {
      if (star[i].half) {
        val = i + 0.5;
        break;
      } else if (star[i].fill && i === star.length - 1) {
        val = star.length;
      } else if (!star[i].fill) {
        val = i;
        break;
      }
    }

    callBack(val);
  }, [callBack, star]);

  const handleMouseLeave = useCallback(() => {
    if (!callBack) return;

    setStar(starList);
  }, [callBack, starList]);

  const isInteractive = callBack !== undefined;

  return (
    <div className="flex flex-start items-center gap-2">
      {star.map((item: StarItem) => (
        <span
          key={item.rate}
          onMouseLeave={isInteractive ? handleMouseLeave : undefined}
          onMouseOver={
            isInteractive ? (e) => handleMouseOver(e, item.rate) : undefined
          }
          onClick={isInteractive ? handleClick : undefined}
          className={isInteractive ? "cursor-pointer" : "cursor-default"}
        >
          {item.fill && !item.half && <BsStarFill color="orange" />}
          {item.fill && item.half && <BsStarHalf color="orange" />}
          {!item.fill && <BsStar />}
        </span>
      ))}
    </div>
  );
};

export default StarRatingComp;
